"use client";

import { c as _c } from "react/compiler-runtime";
import { Fragment, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useGameLocale, useT } from "@/lib/i18n";
import { cachedFetch } from "@/lib/fetch-cache";
import { colorTextClass } from "@/lib/character-colors";
import { restSiteLabel } from "@/lib/rest-site-labels";
import { insightFilterQuery, type InsightFilters } from "@/app/components/ProfileInsights";
import { replaceQuery } from "@/lib/replace-query";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const SMALL_SAMPLE = 20;
const HIDDEN_SAMPLE = 5;
const CATALOG_PATHS = ["/api/cards", "/api/relics", "/api/potions"];
const ENTITY_TYPES = ["cards", "relics", "potions"] as const;
type EntityType = (typeof ENTITY_TYPES)[number];
export type TabId = "cards" | "relics" | "potions" | "events" | "shops" | "campfires";
const TAB_IDS: TabId[] = ["cards", "relics", "potions", "events", "shops", "campfires"];
interface EntityRow {
  id: string;
  runs: number;
  wins: number;
  win_rate: number;
  lift: number | null;
  offered?: number;
  taken?: number;
}
interface EventRow {
  event: string;
  option: string;
  chosen: number;
  wins: number;
  win_rate: number;
}
interface ShopRow {
  entity_type: string;
  id: string;
  seen: number;
  bought: number;
}
interface CampfireRow {
  choice: string;
  chosen: number;
  wins: number;
  win_rate: number;
}
interface PlayerStatsData {
  available: boolean;
  runs: number;
  wins: number;
  tables: {
    cards: EntityRow[];
    relics: EntityRow[];
    potions: EntityRow[];
    events: EventRow[];
    shops: ShopRow[];
    campfires: CampfireRow[];
  };
}
const EMPTY_TABLES: PlayerStatsData["tables"] = {
  cards: [],
  relics: [],
  potions: [],
  events: [],
  shops: [],
  campfires: []
};
interface CatalogEntry {
  id: string;
  name: string;
  color?: string | null;
  type?: string | null;
  rarity?: string | null;
  rarity_key?: string | null;
}
interface EventOption {
  id: string;
  title: string;
  description?: string | null;
}
interface EventCatalogEntry {
  id: string;
  name: string;
  options?: EventOption[] | null;
  pages?: {
    options?: EventOption[] | null;
  }[] | null;
}
interface MetricRow {
  id?: string;
  event?: string;
  option?: string;
  choice?: string;
  name?: string | null;
  win_rate?: number | null;
  lift?: number | null;
  elo?: number | null;
  share?: number | null;
  upgraded?: boolean;
}
interface PickRow {
  id: string;
  name: string;
  sub: string | null;
  color: string | null;
  starter: boolean;
  runs: number;
  winRate: number;
  lift: number | null;
  offered: number | null;
  taken: number | null;
  communityWinRate: number | null;
  communityLift: number | null;
  communityElo: number | null;
}
interface EventOptionRow {
  id: string;
  title: string;
  hint: string | null;
  chosen: number;
  winRate: number;
  share: number;
  communityShare: number | null;
}
interface ShopItemRow {
  id: string;
  name: string;
  sub: string | null;
  color: string | null;
  entityType: EntityType;
  seen: number;
  bought: number;
  buyRate: number | null;
}
interface CampfireChoiceRow {
  id: string;
  name: string;
  chosen: number;
  winRate: number;
  share: number;
}
const EMPTY_EVENTS: EventCatalogEntry[] = [];
interface StatColumn<T> {
  key: string;
  label: string;
  title?: string;
  align: "left" | "right";
  descFirst: boolean;
  value: (r: T) => string | number | null;
  cell: (r: T) => React.ReactNode;
}
interface StatGroup<T> {
  key: string;
  label: string | null;
  note: string | null;
  rows: T[];
}
function cmp(a: unknown, b: unknown, dir: 1 | -1): number {
  const an = a === null || a === undefined;
  const bn = b === null || b === undefined;
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof a === "string" && typeof b === "string") return a.localeCompare(b) * dir;
  return ((a as number) - (b as number)) * dir;
}
function pct(v: number | null): string {
  return v === null || v === undefined ? "–" : `${v.toFixed(1)}%`;
}
function pts(v: number | null): string {
  if (v === null || v === undefined) return "–";
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}`;
}
function int(v: number | null): string {
  return v === null || v === undefined ? "–" : Math.round(v).toLocaleString();
}
function isStarter(c: CatalogEntry | undefined): boolean {
  const key = (c?.rarity_key || c?.rarity || "").toLowerCase();
  const type = (c?.type || "").toLowerCase();
  return key === "starter" || key === "basic" || key === "curse" || type === "curse" || type === "status";
}
const EMPTY_CATALOG: Record<string, CatalogEntry> = {};
function useCatalogs() {
  const $ = _c(4);
  const lang = useGameLocale();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      lang: "",
      map: {}
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== lang) {
    t1 = () => {
      let alive = true;
      Promise.all(CATALOG_PATHS.map(p => cachedFetch(`${API}${p}?lang=${encodeURIComponent(lang)}`).catch(_temp))).then(lists => {
        if (!alive) {
          return;
        }
        const map = {};
        for (const list of lists) {
          for (const c of Array.isArray(list) ? list : []) {
            map[c.id.toUpperCase()] = c;
          }
        }
        setState({
          lang,
          map
        });
      });
      return () => {
        alive = false;
      };
    };
    t2 = [lang];
    $[1] = lang;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return state.lang === lang ? state.map : EMPTY_CATALOG;
}
function _temp() {
  return [];
}
function useApiData(url, t0) {
  const $ = _c(5);
  const withCookies = t0 === undefined ? false : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      url: "",
      data: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== url || $[2] !== withCookies) {
    t2 = () => {
      if (!url) {
        return;
      }
      let alive = true;
      const load = withCookies ? fetch(url, {
        credentials: "include"
      }).then(_temp2) : cachedFetch(url);
      load.then(d => {
        if (alive) {
          setState({
            url,
            data: d
          });
        }
      }).catch(() => {
        if (alive) {
          setState({
            url,
            data: null
          });
        }
      });
      return () => {
        alive = false;
      };
    };
    t3 = [url, withCookies];
    $[1] = url;
    $[2] = withCookies;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  return state.url === url ? state.data : null;
}
function _temp2(r) {
  return r.ok ? r.json() as Promise<T> : null;
}
function MiniBar(t0) {
  const $ = _c(2);
  const {
    value
  } = t0;
  const t1 = `${Math.max(0, Math.min(100, value))}%`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="inline-block h-1.5 w-16 overflow-hidden rounded-full bg-[var(--border-subtle)] align-middle"><span className="block h-full rounded-full bg-[var(--accent-gold)]/70" style={{
        width: t1
      }} /></span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function BarValue(t0) {
  const $ = _c(9);
  const {
    v
  } = t0;
  const t1 = v ?? 0;
  let t2;
  if ($[0] !== t1) {
    t2 = <MiniBar value={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== v) {
    t3 = pct(v);
    $[2] = v;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="w-12">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <span className="inline-flex items-center justify-end gap-2">{t2}{t4}</span>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function LiftValue(t0) {
  const $ = _c(5);
  const {
    v
  } = t0;
  const t1 = v === null ? "text-[var(--text-muted)]" : v > 0 ? "text-success" : v < 0 ? "text-danger" : "";
  let t2;
  if ($[0] !== v) {
    t2 = pts(v);
    $[0] = v;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function NameCell(t0) {
  const $ = _c(10);
  const {
    name,
    sub,
    color
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = colorTextClass(color);
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `font-medium ${t1}`;
  let t3;
  if ($[2] !== name || $[3] !== t2) {
    t3 = <span className={t2}>{name}</span>;
    $[2] = name;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== sub) {
    t4 = sub && <span className="ml-2 text-xs text-[var(--text-muted)]">{sub}</span>;
    $[5] = sub;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function SummaryCard(t0) {
  const $ = _c(12);
  const {
    label,
    value,
    sub,
    title,
    valueClass
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] sm:text-xs">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `mt-1 truncate text-base font-semibold text-[var(--text-primary)] sm:text-lg ${valueClass ?? ""}`;
  let t3;
  if ($[2] !== t2 || $[3] !== value) {
    t3 = <div className={t2}>{value}</div>;
    $[2] = t2;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== sub) {
    t4 = sub && <div className="text-xs text-[var(--text-muted)]">{sub}</div>;
    $[5] = sub;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4 || $[10] !== title) {
    t5 = <div className="min-w-0 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-2 sm:p-3" title={title}>{t1}{t3}{t4}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = title;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
const PREVIEW_ROWS = 10;
const BAR_COLUMNS = new Set(["took", "share", "buyRate"]);
function PersonalStatsTable(t0) {
  const $ = _c(100);
  const {
    columns,
    groups,
    rowKey,
    defaultKey,
    defaultDir
  } = t0;
  const [sortKey, setSortKey] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);
  let t1;
  if ($[0] !== columns || $[1] !== sortKey) {
    t1 = columns.find(c => c.key === sortKey) ?? columns[0];
    $[0] = columns;
    $[1] = sortKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const active = t1;
  let t2;
  if ($[3] !== dir || $[4] !== sortKey) {
    t2 = col => {
      if (col.key === sortKey) {
        setDir(dir === 1 ? -1 : 1);
      } else {
        setSortKey(col.key);
        setDir(col.descFirst ? -1 : 1);
      }
    };
    $[3] = dir;
    $[4] = sortKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onSort = t2;
  let t3;
  if ($[6] !== active || $[7] !== dir || $[8] !== groups || $[9] !== rowKey) {
    let t4;
    if ($[11] !== active || $[12] !== dir || $[13] !== rowKey) {
      t4 = g => ({
        ...g,
        rows: [...g.rows].sort((a, b) => {
          const va = active ? active.value(a) : null;
          const vb = active ? active.value(b) : null;
          const primary = cmp(va, vb, dir);
          if (primary !== 0) {
            return primary;
          }
          return rowKey(a).localeCompare(rowKey(b));
        })
      });
      $[11] = active;
      $[12] = dir;
      $[13] = rowKey;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = groups.map(t4).filter(_temp3);
    $[6] = active;
    $[7] = dir;
    $[8] = groups;
    $[9] = rowKey;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const sorted = t3;
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  let t4;
  if ($[15] !== columns || $[16] !== dir || $[17] !== expanded || $[18] !== onSort || $[19] !== rowKey || $[20] !== sortKey || $[21] !== sorted || $[22] !== t) {
    const total = sorted.reduce(_temp4, 0);
    let t5;
    bb0: {
      if (expanded) {
        t5 = sorted;
        break bb0;
      }
      let left = PREVIEW_ROWS;
      let out;
      if ($[24] !== left || $[25] !== sorted) {
        out = [];
        for (const g_2 of sorted) {
          if (left <= 0) {
            break;
          }
          out.push({
            ...g_2,
            rows: g_2.rows.slice(0, left)
          });
          left = left - g_2.rows.length;
        }
        $[24] = left;
        $[25] = sorted;
        $[26] = out;
        $[27] = left;
      } else {
        out = $[26];
        left = $[27];
      }
      t5 = out;
    }
    const shown = t5;
    let t6;
    let t7;
    let t8;
    if ($[28] !== columns || $[29] !== dir || $[30] !== onSort || $[31] !== rowKey || $[32] !== shown || $[33] !== sortKey || $[34] !== t) {
      const [head, ...rest] = columns;
      t6 = "sm:hidden space-y-2";
      let t9;
      if ($[38] !== t) {
        t9 = t("Sort by");
        $[38] = t;
        $[39] = t9;
      } else {
        t9 = $[39];
      }
      let t10;
      if ($[40] !== t9) {
        t10 = <label htmlFor="ys-sort">{t9}</label>;
        $[40] = t9;
        $[41] = t10;
      } else {
        t10 = $[41];
      }
      let t11;
      if ($[42] !== columns || $[43] !== onSort) {
        t11 = e => {
          const col_0 = columns.find(c_0 => c_0.key === e.target.value);
          if (col_0) {
            onSort(col_0);
          }
        };
        $[42] = columns;
        $[43] = onSort;
        $[44] = t11;
      } else {
        t11 = $[44];
      }
      let t12;
      if ($[45] !== columns) {
        t12 = columns.map(_temp5);
        $[45] = columns;
        $[46] = t12;
      } else {
        t12 = $[46];
      }
      let t13;
      if ($[47] !== sortKey || $[48] !== t11 || $[49] !== t12) {
        t13 = <select id="ys-sort" value={sortKey} onChange={t11} className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-2 py-1 text-xs text-[var(--text-primary)]">{t12}</select>;
        $[47] = sortKey;
        $[48] = t11;
        $[49] = t12;
        $[50] = t13;
      } else {
        t13 = $[50];
      }
      let t14;
      if ($[51] !== dir) {
        t14 = () => setDir(dir === 1 ? -1 : 1);
        $[51] = dir;
        $[52] = t14;
      } else {
        t14 = $[52];
      }
      let t15;
      if ($[53] !== t) {
        t15 = t("Reverse order");
        $[53] = t;
        $[54] = t15;
      } else {
        t15 = $[54];
      }
      const t16 = dir === -1 ? "\u25BE" : "\u25B4";
      let t17;
      if ($[55] !== t14 || $[56] !== t15 || $[57] !== t16) {
        t17 = <button type="button" onClick={t14} aria-label={t15} className="rounded-md border border-[var(--border-subtle)] px-2 py-1 text-xs text-[var(--text-primary)]">{t16}</button>;
        $[55] = t14;
        $[56] = t15;
        $[57] = t16;
        $[58] = t17;
      } else {
        t17 = $[58];
      }
      if ($[59] !== t10 || $[60] !== t13 || $[61] !== t17) {
        t7 = <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">{t10}{t13}{t17}</div>;
        $[59] = t10;
        $[60] = t13;
        $[61] = t17;
        $[62] = t7;
      } else {
        t7 = $[62];
      }
      t8 = shown.map(g_3 => <div key={g_3.key} className="space-y-1.5">{g_3.label !== null && <div className="pt-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{g_3.label}</div>}{g_3.rows.map((r, i) => <div key={rowKey(r)} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)]/60 px-3 py-2"><div className="flex items-baseline gap-2 text-sm"><span className="tabular-nums text-xs text-[var(--text-muted)]">{i + 1}</span><span className="min-w-0">{head.cell(r)}</span></div><dl className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-1 text-xs">{rest.map(col_2 => <div key={col_2.key} className={`min-w-0 ${BAR_COLUMNS.has(col_2.key) ? "col-span-2" : ""}`}><dt className={`truncate text-[10px] uppercase tracking-wide ${col_2.key === sortKey ? "text-[var(--accent-gold)]" : "text-[var(--text-muted)]"}`}>{col_2.label}</dt><dd className="truncate tabular-nums">{col_2.cell(r)}</dd></div>)}</dl></div>)}</div>);
      $[28] = columns;
      $[29] = dir;
      $[30] = onSort;
      $[31] = rowKey;
      $[32] = shown;
      $[33] = sortKey;
      $[34] = t;
      $[35] = t6;
      $[36] = t7;
      $[37] = t8;
    } else {
      t6 = $[35];
      t7 = $[36];
      t8 = $[37];
    }
    let t9;
    if ($[63] !== sorted) {
      t9 = sorted.every(_temp6) && <p className="py-8 text-center text-sm text-[var(--text-muted)]">–</p>;
      $[63] = sorted;
      $[64] = t9;
    } else {
      t9 = $[64];
    }
    let t10;
    if ($[65] !== t6 || $[66] !== t7 || $[67] !== t8 || $[68] !== t9) {
      t10 = <div className={t6}>{t7}{t8}{t9}</div>;
      $[65] = t6;
      $[66] = t7;
      $[67] = t8;
      $[68] = t9;
      $[69] = t10;
    } else {
      t10 = $[69];
    }
    const t11 = `hidden sm:block overflow-x-auto rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-card)]/40 ${expanded ? "max-h-[640px] overflow-y-auto" : ""}`;
    let t12;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <th className="w-10 px-2 py-2 text-right font-medium tabular-nums">#</th>;
      $[70] = t12;
    } else {
      t12 = $[70];
    }
    let t13;
    if ($[71] !== columns || $[72] !== dir || $[73] !== onSort || $[74] !== sortKey) {
      let t14;
      if ($[76] !== dir || $[77] !== onSort || $[78] !== sortKey) {
        t14 = col_3 => <th key={col_3.key} title={col_3.title} onClick={() => onSort(col_3)} className={`cursor-help px-3 py-2 font-medium select-none hover:text-[var(--accent-gold)] ${col_3.align === "right" ? "text-right" : "text-left"} ${col_3.key === sortKey ? "text-[var(--accent-gold)]" : ""}`}>{col_3.label}{col_3.key === sortKey ? dir === -1 ? " \u25BE" : " \u25B4" : ""}</th>;
        $[76] = dir;
        $[77] = onSort;
        $[78] = sortKey;
        $[79] = t14;
      } else {
        t14 = $[79];
      }
      t13 = columns.map(t14);
      $[71] = columns;
      $[72] = dir;
      $[73] = onSort;
      $[74] = sortKey;
      $[75] = t13;
    } else {
      t13 = $[75];
    }
    let t14;
    if ($[80] !== t13) {
      t14 = <thead className="sticky top-0 z-10 bg-[var(--bg-card)] text-[var(--text-secondary)]"><tr className="border-b border-[var(--border-subtle)]">{t12}{t13}</tr></thead>;
      $[80] = t13;
      $[81] = t14;
    } else {
      t14 = $[81];
    }
    let t15;
    if ($[82] !== columns || $[83] !== rowKey || $[84] !== shown) {
      let t16;
      if ($[86] !== columns || $[87] !== rowKey) {
        t16 = g_5 => <Fragment key={g_5.key}>{g_5.label !== null && <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-card)]/70"><td /><td colSpan={columns.length} className="px-3 py-1.5 text-xs font-semibold tracking-wide uppercase text-[var(--text-secondary)]">{g_5.label}{g_5.note && <span className="ml-2 font-normal tracking-normal normal-case text-[var(--text-muted)]">{g_5.note}</span>}</td></tr>}{g_5.rows.map((r_0, i_0) => <tr key={rowKey(r_0)} className="border-b border-[var(--border-subtle)]/40 hover:bg-[var(--bg-card-hover)]/40"><td className="px-2 py-1.5 text-right tabular-nums text-[var(--text-muted)]">{i_0 + 1}</td>{columns.map(col_4 => <td key={col_4.key} className={`px-3 py-1.5 ${col_4.align === "right" ? "text-right tabular-nums" : ""}`}>{col_4.cell(r_0)}</td>)}</tr>)}</Fragment>;
        $[86] = columns;
        $[87] = rowKey;
        $[88] = t16;
      } else {
        t16 = $[88];
      }
      t15 = shown.map(t16);
      $[82] = columns;
      $[83] = rowKey;
      $[84] = shown;
      $[85] = t15;
    } else {
      t15 = $[85];
    }
    let t16;
    if ($[89] !== t15) {
      t16 = <tbody>{t15}</tbody>;
      $[89] = t15;
      $[90] = t16;
    } else {
      t16 = $[90];
    }
    let t17;
    if ($[91] !== t14 || $[92] !== t16) {
      t17 = <table className="w-full min-w-[720px] border-collapse text-sm">{t14}{t16}</table>;
      $[91] = t14;
      $[92] = t16;
      $[93] = t17;
    } else {
      t17 = $[93];
    }
    let t18;
    if ($[94] !== sorted) {
      t18 = sorted.every(_temp7) && <p className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">–</p>;
      $[94] = sorted;
      $[95] = t18;
    } else {
      t18 = $[95];
    }
    let t19;
    if ($[96] !== t11 || $[97] !== t17 || $[98] !== t18) {
      t19 = <div className={t11}>{t17}{t18}</div>;
      $[96] = t11;
      $[97] = t17;
      $[98] = t18;
      $[99] = t19;
    } else {
      t19 = $[99];
    }
    t4 = <>{t10}{t19}{total > PREVIEW_ROWS && <div className="mt-2 text-sm"><button type="button" onClick={() => setExpanded(!expanded)} className="text-[var(--accent-gold)] hover:underline">{expanded ? t("Show less") : t("Show more ({n})", {
            n: total.toLocaleString()
          })}</button></div>}</>;
    $[15] = columns;
    $[16] = dir;
    $[17] = expanded;
    $[18] = onSort;
    $[19] = rowKey;
    $[20] = sortKey;
    $[21] = sorted;
    $[22] = t;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
}
function _temp7(g_6) {
  return g_6.rows.length === 0;
}
function _temp6(g_4) {
  return g_4.rows.length === 0;
}
function _temp5(col_1) {
  return <option key={col_1.key} value={col_1.key}>{col_1.label}</option>;
}
function _temp4(n, g_1) {
  return n + g_1.rows.length;
}
function _temp3(g_0) {
  return g_0.rows.length > 0;
}
const rowKeyOf = (r: {
  id: string;
}) => r.id;
function communityQuery(f: InsightFilters): string {
  const player = {
    "1": "solo",
    "2": "2p",
    "3": "3p",
    "4": "4p"
  }[f.players];
  const parts = [player, f.ascension === "10" ? "a10" : "", f.version].filter(Boolean);
  const params = new URLSearchParams({
    bracket: parts.join(":") || "all"
  });
  if (f.character) params.set("character", f.character);
  return params.toString();
}
function YourStatsInner({
  username,
  filters,
  own,
  fixedTab
}: {
  username: string;
  filters: InsightFilters;
  own: boolean;
  fixedTab?: TabId;
}) {
  const t = useT();
  const lang = useGameLocale();
  const searchParams = useSearchParams();
  const data = useApiData<PlayerStatsData>(own ? `${API}/api/auth/player-stats${insightFilterQuery(filters)}` : `${API}/api/players/${encodeURIComponent(username)}/stats${insightFilterQuery(filters)}`, own);
  const ready = !!data?.available && data.runs > 0;
  const tables = ready && data ? data.tables : EMPTY_TABLES;
  const rawTab = searchParams.get("stats");
  const tab = fixedTab ?? (TAB_IDS.includes(rawTab as TabId) ? rawTab as TabId : "cards");
  const [showStarters, setShowStarters] = useState(false);
  const [showTiny, setShowTiny] = useState(false);
  const [etype, setEtype] = useState<EntityType | "">("");
  const catalog = useCatalogs();
  const communityUrl = ready && tab !== "shops" ? `${API}/api/runs/metrics/${tab}?${communityQuery(filters)}${tab === "campfires" ? `&lang=${encodeURIComponent(lang)}` : ""}` : null;
  const metrics = useApiData<{
    rows?: MetricRow[];
  }>(communityUrl)?.rows ?? null;
  const eventCatalog = useApiData<EventCatalogEntry[]>(ready && tab === "events" ? `${API}/api/events?lang=${encodeURIComponent(lang)}` : null) ?? EMPTY_EVENTS;
  const communityById = useMemo(() => {
    const m = new Map<string, MetricRow>();
    if (metrics) for (const r of metrics) {
      if (r.upgraded) continue;
      const key = (r.id || r.choice || "").toUpperCase();
      if (key) m.set(key, r);
    }
    return m;
  }, [metrics]);
  const communityByEventOption = useMemo(() => {
    const m_0 = new Map<string, number | null>();
    if (metrics) for (const r_0 of metrics) m_0.set(`${(r_0.event || "").toUpperCase()}:${r_0.option || ""}`, r_0.share ?? null);
    return m_0;
  }, [metrics]);
  const picks = useMemo<Record<EntityType, PickRow[]>>(() => {
    const out = {
      cards: [],
      relics: [],
      potions: []
    } as Record<EntityType, PickRow[]>;
    for (const kind of ENTITY_TYPES) {
      out[kind] = tables[kind].map(r_1 => {
        const c = catalog[r_1.id.toUpperCase()];
        if (!c) return null;
        const m_1 = communityById.get(r_1.id.toUpperCase());
        return {
          id: r_1.id,
          name: c.name,
          sub: kind === "cards" ? [c.type, c.rarity].filter(Boolean).join(" · ") || null : c.rarity_key || c.rarity || null,
          color: c.color ?? null,
          starter: isStarter(c),
          runs: r_1.runs,
          winRate: r_1.win_rate,
          lift: r_1.lift,
          offered: r_1.offered ?? null,
          taken: r_1.taken ?? null,
          communityWinRate: m_1?.win_rate ?? null,
          communityLift: m_1?.lift ?? null,
          communityElo: m_1?.elo ?? null
        };
      }).filter(r_2 => r_2 !== null);
    }
    return out;
  }, [catalog, communityById, tables]);
  const eventGroups = useMemo(() => {
    const names = new Map<string, string>();
    const optionsByEvent = new Map<string, Record<string, EventOption>>();
    for (const e of eventCatalog) {
      names.set(e.id.toUpperCase(), e.name);
      const opts: Record<string, EventOption> = {};
      for (const o of e.options ?? []) opts[o.id] = o;
      for (const p of e.pages ?? []) for (const o_0 of p.options ?? []) opts[o_0.id] ??= o_0;
      optionsByEvent.set(e.id.toUpperCase(), opts);
    }
    const byEvent = new Map<string, EventRow[]>();
    for (const r_3 of tables.events) {
      const eid = r_3.event.toUpperCase();
      if (!names.has(eid)) continue;
      const list = byEvent.get(eid);
      if (list) list.push(r_3);else byEvent.set(eid, [r_3]);
    }
    const groups: {
      key: string;
      label: string;
      note: string;
      rows: EventOptionRow[];
    }[] = [];
    for (const [eid_0, list_0] of byEvent) {
      const opts_0 = optionsByEvent.get(eid_0) ?? {};
      const total = list_0.reduce((acc, r_4) => acc + r_4.chosen, 0);
      groups.push({
        key: eid_0,
        label: names.get(eid_0) ?? eid_0,
        note: t("{n} chosen", {
          n: total.toLocaleString()
        }),
        rows: list_0.map((r_5, _i, all) => {
          const opt = opts_0[r_5.option] ?? opts_0[r_5.option.replace(/_\d+$/, "")];
          const base = opt ? opt.title.replace(/\[[^\]]*\]/g, "") : r_5.option;
          const twins = all.filter(o_1 => {
            const oo = opts_0[o_1.option] ?? opts_0[o_1.option.replace(/_\d+$/, "")];
            return (oo ? oo.title.replace(/\[[^\]]*\]/g, "") : o_1.option) === base;
          });
          const tail = r_5.option.match(/_(\d+|[A-Z]+)$/);
          const suffix = twins.length > 1 && tail ? /^\d+$/.test(tail[1]) ? ` · ${Number(tail[1]) + 1}` : ` · ${tail[1].toLowerCase()}` : "";
          return {
            id: `${eid_0}:${r_5.option}`,
            title: base + suffix,
            hint: opt?.description ? opt.description.replace(/\[[^\]]*\]/g, "") : null,
            chosen: r_5.chosen,
            winRate: r_5.win_rate,
            share: total > 0 ? r_5.chosen / total * 100 : 0,
            communityShare: communityByEventOption.get(`${eid_0}:${r_5.option}`) ?? null
          };
        })
      });
    }
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [eventCatalog, communityByEventOption, t, tables]);
  const shops = useMemo<ShopItemRow[]>(() => tables.shops.map(s => {
    const c_0 = catalog[s.id.toUpperCase()];
    if (!c_0) return null;
    return {
      id: `${s.entity_type}:${s.id}`,
      entityType: s.entity_type as EntityType,
      name: c_0.name,
      sub: c_0.rarity_key || c_0.rarity || null,
      color: c_0.color ?? null,
      seen: s.seen,
      bought: s.bought,
      buyRate: s.seen > 0 ? s.bought / s.seen * 100 : null
    };
  }).filter(r_6 => r_6 !== null), [catalog, tables]);
  const campfires = useMemo<CampfireChoiceRow[]>(() => {
    const total_0 = tables.campfires.reduce((acc_0, r_7) => acc_0 + r_7.chosen, 0);
    return tables.campfires.map(r_8 => {
      const m_2 = communityById.get(r_8.choice.toUpperCase());
      return {
        id: r_8.choice.toUpperCase(),
        name: m_2?.name || restSiteLabel(r_8.choice.toUpperCase(), t),
        chosen: r_8.chosen,
        winRate: r_8.win_rate,
        share: total_0 > 0 ? r_8.chosen / total_0 * 100 : 0
      };
    });
  }, [communityById, t, tables]);
  const setTab = useCallback((next: TabId) => {
    const p_0 = new URLSearchParams(searchParams.toString());
    p_0.set("stats", next);
    replaceQuery(p_0.toString());
  }, [searchParams]);
  if (!ready || !data) return null;
  const winTitle = t("Your win rate in the runs that included it.");
  const ranked = [...picks.cards, ...picks.relics, ...picks.potions].filter((r_9): r_9 is PickRow & {
    lift: number;
  } => !r_9.starter && r_9.runs >= SMALL_SAMPLE && r_9.lift !== null);
  const best = ranked.length ? ranked.reduce((a_0, b_0) => b_0.lift > a_0.lift ? b_0 : a_0) : null;
  const worst = ranked.length ? ranked.reduce((a_1, b_1) => b_1.lift < a_1.lift ? b_1 : a_1) : null;
  const chipCls = (activeChip: boolean) => `rounded-full border px-3 py-1 text-xs transition-colors ${activeChip ? "border-[var(--accent-gold)] bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]" : "border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"}`;
  let starterCount = 0;
  let hiddenCount = 0;
  let table: React.ReactNode = null;
  if (tab === "cards" || tab === "relics" || tab === "potions") {
    const rows = picks[tab];
    starterCount = rows.filter(r_10 => r_10.starter).length;
    hiddenCount = rows.filter(r_11 => r_11.runs < HIDDEN_SAMPLE).length;
    const visible = rows.filter(r_12 => (showStarters || !r_12.starter) && (showTiny || r_12.runs >= HIDDEN_SAMPLE));
    const columns: StatColumn<PickRow>[] = [{
      key: "name",
      label: "Name",
      align: "left",
      descFirst: false,
      value: r_13 => r_13.name,
      cell: r_14 => <NameCell name={r_14.name} sub={r_14.sub} color={r_14.color} />
    }, {
      key: "took",
      label: "You took it",
      title: t("Of your {n} runs, how many included it.", {
        n: data.runs.toLocaleString()
      }),
      align: "right",
      descFirst: true,
      value: r_15 => r_15.runs,
      cell: r_16 => <span className="inline-flex items-center justify-end gap-2">
            <MiniBar value={data.runs > 0 ? r_16.runs / data.runs * 100 : 0} />
            <span className="whitespace-nowrap">
              {r_16.runs.toLocaleString()} of {data.runs.toLocaleString()}
            </span>
          </span>
    }, {
      key: "winRate",
      label: "Your Win%",
      title: winTitle,
      align: "right",
      descFirst: true,
      value: r_17 => r_17.winRate,
      cell: r_18 => pct(r_18.winRate)
    }, {
      key: "lift",
      label: "Your Lift",
      title: t("Your win rate minus what you were expected to win from the floor you got it, in percentage points."),
      align: "right",
      descFirst: true,
      value: r_19 => r_19.lift,
      cell: r_20 => <LiftValue v={r_20.lift} />
    }, {
      key: "communityWinRate",
      label: "Community Win%",
      title: t("Win rate across all community-submitted runs."),
      align: "right",
      descFirst: true,
      value: r_21 => r_21.communityWinRate,
      cell: r_22 => pct(r_22.communityWinRate)
    }];
    if (tab !== "potions") {
      columns.push({
        key: "communityElo",
        label: "Community Elo",
        title: t("Codex Elo: how often players take it over the other options on the same screen, fitted as a Bradley-Terry rating."),
        align: "right",
        descFirst: true,
        value: r_23 => r_23.communityElo,
        cell: r_24 => r_24.communityElo === null ? "–" : Math.round(r_24.communityElo).toLocaleString()
      }, {
        key: "communityLift",
        label: "Community Lift",
        title: t("Lift across all community-submitted runs."),
        align: "right",
        descFirst: true,
        value: r_25 => r_25.communityLift,
        cell: r_26 => <LiftValue v={r_26.communityLift} />
      }, {
        key: "takeRate",
        label: "Take rate",
        title: t("How often it was taken when it was offered on a choice screen."),
        align: "right",
        descFirst: true,
        value: r_27 => r_27.offered && r_27.taken !== null ? r_27.taken / r_27.offered : null,
        cell: r_28 => !r_28.offered || r_28.taken === null ? <span className="text-[var(--text-muted)]">–</span> : <span className="whitespace-nowrap">
                {r_28.taken.toLocaleString()} / {r_28.offered.toLocaleString()} ·{" "}
                {pct(r_28.taken / r_28.offered * 100)}
              </span>
      });
    }
    table = <PersonalStatsTable<PickRow> columns={columns} groups={[{
      key: "all",
      label: null,
      note: null,
      rows: visible
    }]} rowKey={rowKeyOf} defaultKey="took" defaultDir={-1} />;
  } else if (tab === "events") {
    const groups_0 = eventGroups.map(g => ({
      key: g.key,
      label: g.label,
      note: g.note,
      rows: g.rows.filter(r_29 => showTiny || r_29.chosen >= HIDDEN_SAMPLE)
    })).filter(g_0 => g_0.rows.length > 0);
    hiddenCount = eventGroups.reduce((acc_1, g_1) => acc_1 + g_1.rows.filter(r_30 => r_30.chosen < HIDDEN_SAMPLE).length, 0);
    const columns_0: StatColumn<EventOptionRow>[] = [{
      key: "option",
      label: "Option",
      align: "left",
      descFirst: false,
      value: r_31 => r_31.title,
      cell: r_32 => <span title={r_32.hint ?? undefined}>{r_32.title}</span>
    }, {
      key: "chosen",
      label: "Chosen",
      align: "right",
      descFirst: true,
      value: r_33 => r_33.chosen,
      cell: r_34 => int(r_34.chosen)
    }, {
      key: "share",
      label: "Share",
      title: t("This option's share of all choices made at this event."),
      align: "right",
      descFirst: true,
      value: r_35 => r_35.share,
      cell: r_36 => <BarValue v={r_36.share} />
    }, {
      key: "winRate",
      label: "Your Win%",
      title: winTitle,
      align: "right",
      descFirst: true,
      value: r_37 => r_37.winRate,
      cell: r_38 => pct(r_38.winRate)
    }, {
      key: "communityShare",
      label: "Community share",
      title: t("This option's share of all community choices at this event."),
      align: "right",
      descFirst: true,
      value: r_39 => r_39.communityShare,
      cell: r_40 => pct(r_40.communityShare)
    }];
    table = <PersonalStatsTable<EventOptionRow> columns={columns_0} groups={groups_0} rowKey={rowKeyOf} defaultKey="chosen" defaultDir={-1} />;
  } else if (tab === "shops") {
    const filtered = etype ? shops.filter(r_41 => r_41.entityType === etype) : shops;
    hiddenCount = shops.filter(r_42 => r_42.bought < HIDDEN_SAMPLE).length;
    const visible_0 = filtered.filter(r_43 => showTiny || r_43.bought >= HIDDEN_SAMPLE);
    const columns_1: StatColumn<ShopItemRow>[] = [{
      key: "name",
      label: "Item",
      align: "left",
      descFirst: false,
      value: r_44 => r_44.name,
      cell: r_45 => <NameCell name={r_45.name} sub={r_45.sub} color={r_45.color} />
    }, {
      key: "seen",
      label: "Seen",
      align: "right",
      descFirst: true,
      value: r_46 => r_46.seen,
      cell: r_47 => int(r_47.seen)
    }, {
      key: "bought",
      label: "Bought",
      align: "right",
      descFirst: true,
      value: r_48 => r_48.bought,
      cell: r_49 => int(r_49.bought)
    }, {
      key: "buyRate",
      label: "Buy rate",
      align: "right",
      descFirst: true,
      value: r_50 => r_50.buyRate,
      cell: r_51 => <BarValue v={r_51.buyRate} />
    }];
    table = <PersonalStatsTable<ShopItemRow> columns={columns_1} groups={[{
      key: "all",
      label: null,
      note: null,
      rows: visible_0
    }]} rowKey={rowKeyOf} defaultKey="bought" defaultDir={-1} />;
  } else {
    hiddenCount = campfires.filter(r_52 => r_52.chosen < HIDDEN_SAMPLE).length;
    const visible_1 = campfires.filter(r_53 => showTiny || r_53.chosen >= HIDDEN_SAMPLE);
    const columns_2: StatColumn<CampfireChoiceRow>[] = [{
      key: "name",
      label: "Choice",
      align: "left",
      descFirst: false,
      value: r_54 => r_54.name,
      cell: r_55 => <NameCell name={r_55.name} sub={null} color={null} />
    }, {
      key: "chosen",
      label: "Chosen",
      align: "right",
      descFirst: true,
      value: r_56 => r_56.chosen,
      cell: r_57 => int(r_57.chosen)
    }, {
      key: "share",
      label: "Share",
      title: t("This action's share of all choices made at campfires."),
      align: "right",
      descFirst: true,
      value: r_58 => r_58.share,
      cell: r_59 => <BarValue v={r_59.share} />
    }, {
      key: "winRate",
      label: "Your Win%",
      title: winTitle,
      align: "right",
      descFirst: true,
      value: r_60 => r_60.winRate,
      cell: r_61 => pct(r_61.winRate)
    }];
    table = <PersonalStatsTable<CampfireChoiceRow> columns={columns_2} groups={[{
      key: "all",
      label: null,
      note: null,
      rows: visible_1
    }]} rowKey={rowKeyOf} defaultKey="chosen" defaultDir={-1} />;
  }
  return <section className="space-y-4">
      {!fixedTab && <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h2 className="text-xl font-bold text-[var(--text-primary)]">
            {t("Your stats")}
          </h2>
          <nav aria-label={t("Your stats")} className="flex flex-wrap items-center gap-1.5 text-xs">
            {TAB_IDS.map(id => <button key={id} type="button" onClick={() => setTab(id)} className={`rounded-md border px-2.5 py-1 transition-colors ${id === tab ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-accent)] hover:text-[var(--text-primary)]"}`}>
                {t(id.charAt(0).toUpperCase() + id.slice(1))}
              </button>)}
          </nav>
        </div>}

      <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:grid-cols-5">
        <SummaryCard label={t("Runs")} value={int(data.runs)} />
        <SummaryCard label={t("Wins")} value={int(data.wins)} />
        <SummaryCard label={t("Win rate")} value={pct(data.runs > 0 ? data.wins / data.runs * 100 : null)} />
        <SummaryCard label={t("Best pick")} value={best ? best.name : "–"} sub={best ? pts(best.lift) : undefined} valueClass={best ? colorTextClass(best.color) : undefined} title={t("Your best Lift among picks with at least {min} runs.", {
        min: SMALL_SAMPLE
      })} />
        <SummaryCard label={t("Worst pick")} value={worst ? worst.name : "–"} sub={worst ? pts(worst.lift) : undefined} valueClass={worst ? colorTextClass(worst.color) : undefined} title={t("Your worst Lift among picks with at least {min} runs.", {
        min: SMALL_SAMPLE
      })} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {tab === "shops" && <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setEtype("")} className={chipCls(etype === "")}>
              {t("All")}
            </button>
            {ENTITY_TYPES.map(e_0 => <button key={e_0} type="button" onClick={() => setEtype(e_0)} className={chipCls(etype === e_0)}>
                {t(e_0.charAt(0).toUpperCase() + e_0.slice(1))}
              </button>)}
          </div>}
        {starterCount > 0 && <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]" title={t("You start every run with these, so take counts are meaningless.")}>
            <input type="checkbox" checked={showStarters} onChange={e_1 => setShowStarters(e_1.target.checked)} />
            {t("Show starters and curses")}
          </label>}
        {hiddenCount > 0 && <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <input type="checkbox" checked={showTiny} onChange={e_2 => setShowTiny(e_2.target.checked)} />
            {t("Show rows under 5 runs")}
          </label>}
      </div>

      {table}
    </section>;
}

/** useSearchParams needs a Suspense boundary above it now that the root
 * layout no longer provides one; keep it local to this section. */
export default function YourStats(t0) {
  const $ = _c(5);
  const {
    username,
    filters,
    own: t1,
    tab
  } = t0;
  const own = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== filters || $[1] !== own || $[2] !== tab || $[3] !== username) {
    t2 = <Suspense fallback={null}><YourStatsInner username={username} filters={filters} own={own} fixedTab={tab} /></Suspense>;
    $[0] = filters;
    $[1] = own;
    $[2] = tab;
    $[3] = username;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}